import { TextField, IconButton, Paper, Box, Button } from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import LanguageIcon from "@mui/icons-material/Language";
import SendIcon from "@mui/icons-material/Send";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import Settings from "@mui/icons-material/Settings";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";
import PublicIcon from "@mui/icons-material/Public";
import TravelExploreIcon from "@mui/icons-material/TravelExplore";
import { memo, useEffect, useState } from "react";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import { CloseOutlined } from "@mui/icons-material";
import { StopCircle } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

import { ChatLoadingTimer } from "../ui/chat-loading-timer";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import SystemPrompt from "@/components/copilot/additional-prompt";
import { rephrasePrompt } from "../../api/copilot/prompt.api";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import useCopilotStore from "@/store/copilotStore";
import { DEFAULT_ADDITIONAL_PROMPT } from "@/constants/copilot";

const ChatInput = memo(
  ({
    userPrompt,
    handlePromptChange,
    fileStates,
    setUserPrompt,
    handleSendUserQuery,
    additionalPrompt,
    setAdditionalPrompt,
    selectedSource,
    setSelectedSource,
    chatLoading,
    stopChat,
    fileInputRef,
    handleFileSelect,
    setFileStates,
    fileError,
    setFileError,
    systemPrompt,
    setSystemPrompt,
    additionalPromptOpen,
    setAdditionalPromptOpen,
    handleSystemPromptUpdate,
    conversationId,
    }: any) => {
    const [originalUserPrompt, setOriginalUserPrompt] = useState("");
    const [showRevert, setShowRevert] = useState(false);
    
    const sourceOptions = [
      { option: "Knowledge Base", tooltip: "Refer Internal Documents" },
      { option: "Web", tooltip: "Search the Internet" },
      {
        option: "Knowledge Base and Web",
        tooltip: "To refer internal documents and Web",
      },
      { option: "None", tooltip: "Uses no sources of information" },
    ];

    const copilotStore = useCopilotStore();

    const [searchParams] = useSearchParams();
    const conversationType = searchParams.get("convType");

    useEffect(() => {
      if (chatLoading) {
        setShowRevert(false);
      }
    }, [chatLoading]);

    const handleRephrase = async () => {
      if (!userPrompt) return;
      try {
        if (!showRevert) {
          setOriginalUserPrompt(userPrompt);
        }
        const data = await rephrasePrompt(userPrompt, conversationId);
        if (data.rephrased_queries && data.rephrased_queries.length > 1) {
          setUserPrompt(data.rephrased_queries[1]);
          if (userPrompt !== data.rephrased_queries[1]) {
            setShowRevert(true);
          }
        } else if (data.rephrased_queries && data.rephrased_queries.length > 0) {
          setUserPrompt(data.rephrased_queries[0]);
          if (userPrompt !== data.rephrased_queries[0]) {
            setShowRevert(true);
          }
        }
      } catch (error) {
        console.error("Error in handleRephrase:", error);
      }
    };

    function getIcon(option: string, sx?: any) {
      switch (option) {
        case "Knowledge Base":
          return <LibraryBooksIcon sx={sx} />;
        case "Web":
          return <PublicIcon sx={sx} />;
        case "Knowledge Base and Web":
          return <TravelExploreIcon sx={sx} />;
        case "None":
          return <SearchOffIcon sx={sx} />;
        default:
          return <LanguageIcon sx={sx} />;
      }
    }

    return (
      <Paper
        elevation={3}
        sx={{
          display: "flex",
          flexDirection: "column",
          padding: "1rem 1rem",
          borderRadius: "0.75rem",
          width: "100%",
          maxWidth: "60rem",
        }}
      >
        <Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          gap={1}
          width="100%"
        >
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <IconButton onClick={handleRephrase}>
                  <AutoFixHighIcon className="ml-3 mb-1" />
                </IconButton>
              </TooltipTrigger>
              <TooltipContent className="bg-black">
                <p className="text-white">Fix my prompt</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <TextField
            fullWidth
            variant="standard"
            placeholder="Ask Assistant"
            InputProps={{
              disableUnderline: true,
            }}
            sx={{
              ml: 1,
              flex: 1,
              maxHeight: "40vh",
              overflow: "auto",
            }}
            autoFocus
            value={userPrompt}
            onChange={handlePromptChange}
            multiline
            disabled={chatLoading}
          />
          {userPrompt && (
            <Button onClick={handleRephrase} size="small" variant="outlined">
              Fix My Prompt
            </Button>
          )}
          {showRevert && (
            <Button
              onClick={() => {
                setUserPrompt(originalUserPrompt);
                setShowRevert(false);
              }}
              size="small"
              variant="outlined"
            >
              Use Original Query
            </Button>
          )}
        </Box>
        {fileStates?.length > 0 && (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="flex-start"
            width="100%"
            mt={1}
          >
            {fileStates.map((fileState: any, index: any) => (
              <Box
                key={`${fileState?.name}-${index}`}
                display="flex"
                flexDirection="row"
                alignItems="center"
                justifyContent="space-between"
                width="100%"
                mt={1}
              >
                <div className="border rounded-sm p-1 w-full">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <AttachFileIcon />
                      <span className="ml-1 mr-2">{fileState?.file?.name}</span>
                      {fileState.progress < 100 && !fileState.error && (
                        <span className="text-xs text-muted-foreground">
                          {fileState.progress}%
                        </span>
                      )}
                      {fileState.error && (
                        <span className="text-xs text-red-500">
                          Upload failed
                        </span>
                      )}
                      {fileState.preview && (
                        <img
                          src={fileState.preview}
                          alt={`Preview of ${fileState.file.name}`}
                          style={{
                            maxHeight: "2.5rem",
                            maxWidth: "2.5rem",
                            objectFit: "contain",
                          }}
                        />
                      )}
                    </div>
                    <IconButton
                      onClick={() => {
                        setFileStates((prev: any) =>
                          prev.filter((_: any, i: any) => i !== index)
                        );
                        copilotStore.setFileError("");
                      }}
                    >
                      <CloseOutlined className="max-h-4" />
                    </IconButton>
                  </div>

                  {fileState.progress < 100 && !fileState.error && (
                    <div className="w-full bg-muted h-1 mt-1 rounded-full overflow-hidden">
                      <div
                        className="bg-brand-secondary h-full"
                        style={{ width: `${fileState.progress}%` }}
                      ></div>
                    </div>
                  )}

                  {fileState.progress === 100 && !fileState.error && (
                    <div className="text-xs text-green-500 mt-1">
                      Upload complete
                    </div>
                  )}
                </div>
              </Box>
            ))}
          </Box>
        )}

        {fileError ||
          (copilotStore?.fileError && (
            <Box
              sx={{
                color: "error.main",
                fontSize: "0.75rem",
                mt: 1,
                mb: 1,
                fontWeight: "500",
              }}
            >
              {fileError} {copilotStore?.fileError}
            </Box>
          ))}

        <Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
          width="100%"
          mt={1}
        >
          <Box display="flex" gap={1} alignItems="center">
            <input
              data-testid="file-input"
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.txt,.xls,.xlsx,.doc,.docx"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={handleFileSelect}
              multiple
            />
            {conversationType !== "compare" && (
              <>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger
                      asChild
                      onClick={() => {
                        if (fileStates.length >= 3) {
                          setFileError("Maximum of three files allowed");
                          copilotStore.setFileError(
                            "Maximum of three files allowed"
                          );
                          return;
                        }
                        setFileError("");
                        copilotStore.setFileError("");
                        fileInputRef.current?.click();
                      }}
                    >
                      {/* --- MODIFICATION: ATTACH FILE BUTTON --- */}
                      <Box
                        display="flex"
                        alignItems="center"
                        gap={0.5}
                        sx={{
                          cursor: "pointer",
                          padding: "0.5rem",
                          borderRadius: "0.5rem",
                          "&:hover": {
                            backgroundColor: "rgba(0, 0, 0, 0.05)",
                          },
                        }}
                      >
                        <AttachFileIcon
                          className={`${
                            fileStates?.length ? "text-brand-secondary" : ""
                          }`}
                        />
                        {/* --- DYNAMIC TEXT FOR FILE COUNT --- */}
                        <span className="text-sm font-medium">
                          Attach
                          {fileStates.length > 0 && ` (${fileStates.length})`}
                        </span>
                      </Box>
                    </TooltipTrigger>
                    <TooltipContent className="bg-black">
                      <ul className="text-white list-disc pl-4">
                        <li>Attach files (max. 3)</li>
                        <li>
                          Data loss is a potential risk during Excel file
                          processing.
                        </li>
                        <li>Each file should be below 20MB</li>
                        <li>
                          Supported file types are .pdf, .jpg, .png, .txt, .doc,
                          .docx, .xls, .xlsx*
                        </li>
                        <li>
                          Disclaimer: Uploaded files are ephemeral to this chat
                          and will not be added to the knowledge base.
                        </li>
                      </ul>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>

                <TooltipProvider>
                  <DropdownMenu>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <DropdownMenuTrigger asChild>
                          {/* --- MODIFICATION: SOURCE OF INFO BUTTON --- */}
                          <Box
                            display="flex"
                            alignItems="center"
                            gap={0.5}
                            sx={{
                              cursor: "pointer",
                              padding: "0.5rem",
                              borderRadius: "0.5rem",
                              "&:hover": {
                                backgroundColor: "rgba(0, 0, 0, 0.05)",
                              },
                            }}
                          >
                            {getIcon(
                              selectedSource,
                              {
                                color: `${
                                  selectedSource !== "None"
                                    ? "var(--colors-brand-secondary)"
                                    : ""
                                }`,
                              }
                            )}
                            {/* --- DYNAMIC TEXT FOR SELECTED SOURCE --- */}
                            <span className="text-sm font-medium">
                              {selectedSource}
                            </span>
                            <ArrowDropDownIcon />
                          </Box>
                        </DropdownMenuTrigger>
                      </TooltipTrigger>
                      {/* --- MODIFICATION: DYNAMIC TOOLTIP --- */}
                      <TooltipContent className="bg-black">
                        <p className="text-white">
                          Current source: {selectedSource}. Click to change.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                    <DropdownMenuContent>
                      <DropdownMenuLabel>Sources</DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      {sourceOptions?.map(({ option, tooltip }) => (
                        <Tooltip key={option}>
                          <TooltipTrigger asChild>
                            <DropdownMenuItem
                              onClick={() => setSelectedSource(option)}
                              className={`
                                ${selectedSource === option
                                    ? "bg-brand-primary text-accent-foreground focus:bg-brand-secondary text-white focus:text-white"
                                    : "focus:bg-brand-primary focus:text-white"
                                }
                              `}
                            >
                              {getIcon(option)}
                              <span className="ml-2">{option}</span>
                            </DropdownMenuItem>
                          </TooltipTrigger>
                          {tooltip !== "" && (
                            <TooltipContent
                              className="bg-black"
                              side="right"
                            >
                              <p className="text-white">{tooltip}</p>
                            </TooltipContent>
                          )}
                        </Tooltip>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TooltipProvider>

                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger
                      asChild
                      onClick={() => setAdditionalPromptOpen(true)}
                    >
                      <Box
                        display="flex"
                        alignItems="center"
                        gap={0.5}
                        sx={{
                          cursor: "pointer",
                          padding: "0.5rem",
                          borderRadius: "0.5rem",
                          "&:hover": {
                            backgroundColor: "rgba(0, 0, 0, 0.05)",
                          },
                        }}
                      >
                        <Settings
                          className={`${systemPrompt !== DEFAULT_ADDITIONAL_PROMPT ? "text-brand-secondary" : ""}`}
                        />
                        <span className="text-sm font-medium">System Prompt</span>
                      </Box>
                    </TooltipTrigger>
                    <TooltipContent className="bg-black">
                      <p className="text-white">
                        Use System Prompt to set the overall behavior for the
                        model
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </>
            )}
          </Box>

          <Box display="flex" alignItems="center" gap={2}>
            {userPrompt?.length > 3 && (
              <div className="text-xs text-muted-foreground whitespace-nowrap">
                Shift + Enter for new line
              </div>
            )}

            <div className="min-h-6">
              <ChatLoadingTimer className="text-muted-foreground" />
            </div>

            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  {chatLoading ? (
                    <Button
                      variant="text"
                      sx={{
                        cursor: "pointer",
                        padding: "0.5rem",
                        borderRadius: "0.5rem",
                        "&:hover": {
                          backgroundColor: "rgba(0, 0, 0, 0.05)",
                        },
                      }}
                      onClick={stopChat}
                      disabled={!copilotStore?.stopMessageId}
                      startIcon={<StopCircle />}
                    >
                      <span className="text-sm font-medium">
                        Stop
                      </span>
                    </Button>
                  ) : (
                    <Button
                      variant="text"
                      sx={{
                        cursor: "pointer",
                        padding: "0.5rem",
                        borderRadius: "0.5rem",
                        "&:hover": {
                          backgroundColor: "rgba(0, 0, 0, 0.05)",
                        },
                      }}
                      onClick={() => {
                        handleSendUserQuery();
                      }}
                      startIcon={<SendIcon />}
                    >
                      <span className="text-sm font-medium">
                        Generate
                      </span>
                    </Button>
                  )}
                </TooltipTrigger>

                <TooltipContent className="bg-black">
                  <p>
                    {chatLoading ? (
                      <div className="text-white">Stop</div>
                    ) : (
                      <div className="flex text-white">Generate</div>
                    )}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </Box>
        </Box>
        <SystemPrompt
          isOpen={additionalPromptOpen}
          onClose={() => setAdditionalPromptOpen(false)}
          onConfirm={() => {
            setAdditionalPromptOpen(false);
            handleSystemPromptUpdate(systemPrompt);
          }}
          additionalPrompt={additionalPrompt}
          setAdditionalPrompt={setAdditionalPrompt}
          systemPrompt={systemPrompt}
          setSystemPrompt={setSystemPrompt}
          handleSystemPromptUpdate={handleSystemPromptUpdate}
        />
      </Paper>
    );
  }
);

export default ChatInput;